// Chat seguro firma↔cliente (VLT-214 · migraciones 0037/0042). Un hilo por
// cliente: el staff escribe desde el perfil o /messages; el cliente, desde su
// portal. Mensajes INMUTABLES (constancia), leído vía RPC. Fase 2: REALTIME
// vía postgres_changes (respeta la RLS del suscriptor) con sondeo de respaldo
// por si el canal en vivo no está disponible.
import { supabase, hasBackend } from '@/lib/supabase';
import { useSession } from '@/stores/session';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export interface ChatMessage {
  id: string;
  body: string;
  mine: boolean;
  staffSide: boolean;
  authorName: string;
  createdAt: string;
}

export interface MessagesService {
  /** Hilo de un cliente (staff) o el propio (cliente — ignora el parámetro). */
  thread(clientId?: string): Promise<{ clientId: string; messages: ChatMessage[] } | null>;
  send(clientId: string, body: string): Promise<boolean>;
  /** Marca leídos los mensajes del OTRO lado; devuelve cuántos. */
  markRead(clientId: string): Promise<number>;
  /** No leídos que me esperan (cliente: del despacho; staff: de sus clientes). */
  unread(): Promise<number>;
  /** Realtime (fase 2): avisa cuando entra un mensaje — del hilo indicado, o
   *  de cualquiera visible bajo mi RLS si no se indica. Devuelve el
   *  desuscriptor. Si realtime no está disponible, simplemente no avisa (el
   *  sondeo de respaldo sigue cubriendo). */
  subscribe(onNew: () => void, clientId?: string): () => void;
}

interface Me {
  uid: string;
  role: string;
  orgId: string;
  /** id de la ficha clients cuando el usuario ES cliente del portal. */
  clientId: string | null;
}

let meCache: Me | null = null;
async function me(): Promise<Me | null> {
  if (meCache) return meCache;
  const { data: auth } = await sessionUser();
  if (!auth.user) return null;
  const { data: prof } = await supabase
    .from('profiles').select('role, organization_id').eq('id', auth.user.id).maybeSingle();
  const p = prof as { role: string; organization_id: string } | null;
  if (!p) return null;
  let clientId: string | null = null;
  if (p.role === 'client') {
    const { data: c } = await supabase
      .from('clients').select('id').eq('profile_id', auth.user.id).maybeSingle();
    clientId = (c as { id: string } | null)?.id ?? null;
  }
  meCache = { uid: auth.user.id, role: p.role, orgId: p.organization_id, clientId };
  return meCache;
}

interface Row {
  id: string; body: string; author_id: string; author_is_staff: boolean; created_at: string;
  author: { full_name: string | null } | null;
}

const realMessages: MessagesService = {
  async thread(clientId) {
    const m = await me();
    if (!m) return null;
    const cid = m.role === 'client' ? m.clientId : (clientId ?? null);
    if (!cid) return null;
    const { data, error } = await supabase
      .from('messages')
      .select('id, body, author_id, author_is_staff, created_at, author:profiles!author_id(full_name)')
      .eq('client_id', cid)
      .order('created_at', { ascending: true })
      .limit(200);
    if (error) loadFailed('messages.thread', error);
    return {
      clientId: cid,
      messages: (data as unknown as Row[]).map((r) => ({
        id: r.id, body: r.body, mine: r.author_id === m.uid,
        staffSide: r.author_is_staff,
        authorName: r.author?.full_name ?? '—', createdAt: r.created_at,
      })),
    };
  },

  async send(clientId, body) {
    const m = await me();
    const text = body.trim();
    if (!m || !text) return false;
    const cid = m.role === 'client' ? m.clientId : clientId;
    if (!cid) return false;
    const { error } = await supabase.from('messages').insert({
      organization_id: m.orgId, client_id: cid, author_id: m.uid,
      author_is_staff: m.role !== 'client', body: text.slice(0, 4000),
    } as never);
    if (error) console.warn('[VaulTex] messages.send:', error.message);
    return !error;
  },

  async markRead(clientId) {
    const m = await me();
    const cid = m?.role === 'client' ? m.clientId : clientId;
    if (!cid) return 0;
    const { data, error } = await supabase.rpc('mark_messages_read', { p_client: cid } as never);
    if (error) {
      console.warn('[VaulTex] messages.markRead:', error.message);
      return 0;
    }
    return (data as number) ?? 0;
  },

  subscribe(onNew, clientId) {
    const ch = supabase
      .channel(`vaultex-messages-${clientId ?? 'all'}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT', schema: 'vaultex', table: 'messages',
          ...(clientId ? { filter: `client_id=eq.${clientId}` } : {}),
        },
        () => onNew(),
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  },

  async unread() {
    const m = await me();
    if (!m) return 0;
    const { count } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('author_is_staff', m.role === 'client')
      .is('read_at', null);
    return count ?? 0;
  },
};

// ─── Demo (fixtures; «mine» depende de QUIÉN mira — rol de la sesión demo) ───

const demoIsStaff = () => (useSession.getState().role ?? 'client') !== 'client';

const DEMO_MSGS: Omit<ChatMessage, 'mine'>[] = [
  { id: 'mg1', body: 'Hola María, para cerrar tu declaración solo falta el W-2 de 2025. ¿Lo puedes subir esta semana?', staffSide: true, authorName: 'Gabriela C.', createdAt: '2026-09-10T14:05:00Z' },
  { id: 'mg2', body: 'Claro, lo pido en mi trabajo mañana.', staffSide: false, authorName: 'María Santos', createdAt: '2026-09-10T16:22:00Z' },
  { id: 'mg3', body: 'Perfecto. Cuando lo subas al portal, me llega el aviso y seguimos. ¡Gracias!', staffSide: true, authorName: 'Gabriela C.', createdAt: '2026-09-10T16:30:00Z' },
];
let demoUnread = 1;

const fixtureMessages: MessagesService = {
  thread: (clientId) => {
    const staff = demoIsStaff();
    const msgs = DEMO_MSGS.map((m) => ({ ...m, mine: m.staffSide === staff }));
    return Promise.resolve(
      !clientId || clientId === 'me' || clientId === 'c1' ? { clientId: 'c1', messages: msgs } : { clientId, messages: [] },
    );
  },
  send: (clientId, body) => {
    const staff = demoIsStaff();
    if (clientId === 'c1' || clientId === 'me' || !clientId) {
      DEMO_MSGS.push({
        id: `mg${Date.now()}`, body: body.trim(), staffSide: staff,
        authorName: staff ? 'Tú (despacho)' : 'María Santos', createdAt: new Date().toISOString(),
      });
    }
    return Promise.resolve(true);
  },
  markRead: () => {
    const n = demoUnread; demoUnread = 0;
    return Promise.resolve(n);
  },
  unread: () => Promise.resolve(demoUnread),
  subscribe: () => () => {},
};

export const messagesService: MessagesService = hasBackend ? realMessages : fixtureMessages;
